import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Button, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import EmptyComponent from "../../../../../Components/Empty"
import Table from "../../../../../Components/Table"
import { RoutesPaths } from "../../../../../constants/user-routes"
import Creators from "../../reducer"
import AddEndpointModal from "../Modals/AddEndpointModal"
import AddEventTypeModal from "../Modals/AddEventTypeModal"
import { defaultColumns, getActionColumn, getColumns, getDefaultColumn } from "./columns"

import "../webhooks.less"

const { Text } = Typography

// const endpoints = {
//   count: 2,
//   results: [
//         {
//           "id": "6232a61b-1888-4c56-b215-5b16f8cb0d64",
//           "enpoint": "https://webhook.site/#!/8f4f3c6e-6f4e-4f6a-9f7d-5c6e8f4f3c6e",
//           "error_rate": "0.01",
//           "type": ["Type 1", "Type 2"],
//           "status": "active",
//         },
//         {
//           "id": "6232a61b-1888-4c56-b215-5b16f8cb0d65",
//           "enpoint": "https://webhook.site/#!/8f4f3c6e-6f4e-4f6a-9f7d-5c6e8f4f3c64",
//           "error_rate": "0.05",
//           "type":  ["Type 1", "Type 2", "Type 3", "Type 4"],
//           "status": "disabled",
//         },
//       ]
// }

const EndpointsTable = ({
  modalType,
  setModalType,
  eventTypeOptions: settingsEventTypeOptions = [],
  openAddEndpointModal
}) => {
  const dispatch = useDispatch()
  const { notification } = App.useApp()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { webhooksFetching, endpoints } = useSelector((state) => state.publicApi)
  const { theme } = useSelector((state) => state.nav)
  const count = endpoints?.count || 0
  const pageSize = 10
  const results = endpoints?.results || []
  const eventTypeOptions = useMemo(() => {
    if ((settingsEventTypeOptions || []).length) {
      return settingsEventTypeOptions.map((option) => ({
        value: option.value,
        label: option.label || option.value,
        is_selected: false
      }))
    }

    const optionsMap = new Map()

    results.forEach((endpoint) => {
      endpoint?.event_type_options?.forEach((option) => {
        if (option?.value && !optionsMap.has(option.value)) {
          optionsMap.set(option.value, {
            value: option.value,
            label: option.label,
            is_selected: Boolean(option.is_selected)
          })
        }
      })
    })

    return Array.from(optionsMap.values())
  }, [settingsEventTypeOptions, results])
  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)

  const myColumns = defaultColumns

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: "auto"
        // y: 'calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))',
        // scrollToFirstRowOnChange: true,
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: (
          <EmptyComponent type="no_data_available_webhooks_endpoints">
            <Button
              size="large"
              type="primary"
              onClick={openAddEndpointModal}
              style={{ marginTop: "8px" }}
            >
              {"Add Endpoint"}
            </Button>
          </EmptyComponent>
        )
      }
    }),
    [count, pageNumber]
  )

  function handleSelectRow(data, type) {
    if (type === "add_event") {
      const selectedTypes = new Set(
        (data?.event_types || []).map((item) => item?.event_type).filter(Boolean)
      )
      const enrichedOptions = eventTypeOptions.map((option) => ({
        value: option.value,
        label: option.label || option.value,
        is_selected: selectedTypes.has(option.value)
      }))

      setModalType({
        type,
        data: {
          ...data,
          event_type_options: enrichedOptions
        }
      })
    }
  }

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const getTableColumns = useMemo(
    (type) => {
      let allColumns = getColumns({ theme })

      const actionColumn = getActionColumn({
        onSelectRow: handleSelectRow
      })

      return [
        ...myColumns
          .filter(({ active }) => active)
          .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
        ...(actionColumn ? [actionColumn] : [])
      ]
    },
    [theme]
  )

  const updateData = () => {
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering
    }
    // console.log('updateData')
    dispatch(Creators.getEndpointsRequest({ ...data }))
  }

  const openEdpointDetails = (endpointData) => {
    const endpointId =
      endpointData?.endpoint_id || endpointData?.id || endpointData?.event_types?.[0]?.id

    if (endpointData?.callback_url && endpointId) {
      navigate(`${RoutesPaths.settings.developers_webhooks}/endpoints/${endpointId}`)
    }
  }

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  const onAddEventTypeSuccess = () => {
    setModalType(null)
    updateData()
    notification.success({
      message: t("Event type added successfully"),
      ...notificationConfig
    })
  }

  const onAddEnpointSuccess = () => {
    setModalType(null)
    updateData()
    notification.success({
      message: t("Endpoint added successfully"),
      ...notificationConfig
    })
  }

  useEffect(() => {
    updateData()
  }, [ordering, pageNumber])

  return (
    <div style={{ marginTop: 21 }}>
      <Table
        columns={getTableColumns}
        loading={webhooksFetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        //withOutPagination={true}
        locale={tableSettings.locale}
        rowHoverable={true}
        onRow={(record) => ({
          onClick: (event) => {
            openEdpointDetails(record)
          }
        })}
      />
      {modalType?.type === "add_event" && (
        <AddEventTypeModal
          modalType={modalType}
          close={() => setModalType(null)}
          onAddEventTypeSuccess={onAddEventTypeSuccess}
        />
      )}
      {modalType?.type === "add_endpoint" && (
        <AddEndpointModal
          modalType={modalType}
          close={() => setModalType(null)}
          onAddEnpointSuccess={onAddEnpointSuccess}
          eventTypeOptions={eventTypeOptions}
        />
      )}
    </div>
  )
}

export default EndpointsTable
